import classNames from 'classnames';
import { FieldArrayWithId, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { ApiDataFieldKey, JsonSwitchProps } from './ApiDataTable';
import { ApiDataItemDto } from '@/Domain/api/Action/dto/Action.dto';
import { ApiDataItem } from '@/Domain/components/Action/ApiDataTable/ApiDataItem';
import { JsonEditor } from '@/Domain/components/Action/Lexical/JsonEditor';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FormControl, FormField, FormItem } from '@/Domain/components/Shared/Form/Form';
import AddIcon from '@/Domain/components/icons/AddIcon';

type Props = {
  name: string;
  fields: FieldArrayWithId<ApiDataItemDto>[];
  dataFieldKey: ApiDataFieldKey;
  withInsertBadge: boolean;
  onRemove: (index: number) => void;
  onAdd: () => void;
  jsonSwitchParams?: JsonSwitchProps;
};

export const ApiDataFields = ({
  name,
  fields,
  onRemove,
  onAdd,
  dataFieldKey,
  withInsertBadge,
  jsonSwitchParams,
}: Props) => {
  const { t } = useTranslation('actions_page');

  const parameters = useWatch({ name: 'parameters' });

  return (
    <>
      <div
        className={classNames('mt-4', {
          block: jsonSwitchParams?.isActive,
          hidden: !jsonSwitchParams?.isActive,
        })}
      >
        <FormField
          name={`${name}_json`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <JsonEditor field={field} parameters={parameters} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div
        className={classNames('flex-1 flex flex-col gap-3 items-center justify-center mt-7', {
          hidden: fields.length > 0 || jsonSwitchParams?.isActive,
        })}
      >
        <p className="text-sm font-medium text-foreground">
          {t('sheet.form.api_endpoint.api_data.empty.title')}
        </p>
        <p className="text-center text-sm text-muted-foreground">
          <Trans i18nKey="sheet.form.api_endpoint.api_data.empty.description" t={t} />
        </p>
        <Button onClick={onAdd}>
          <AddIcon />
          {t('button.add_item')}
        </Button>
      </div>

      <div
        className={classNames('space-y-2', {
          hidden: jsonSwitchParams?.isActive || fields.length === 0,
        })}
      >
        {fields.map((field, index) => (
          <div key={field.id}>
            <ApiDataItem
              name={name}
              index={index}
              arrayField={field}
              dataFieldKey={dataFieldKey}
              withInsertBadge={withInsertBadge}
              onRemove={onRemove}
            />
          </div>
        ))}
      </div>
    </>
  );
};
